import { AsyncService } from '../services/async-service';
import { Directive } from '../classes';

/**
 * @HowToUse:
 * 1. you should add an async capacity to switch construction in file ./src/async
 *    @E.g.
 *    ... some code
 *    switch(name){
 *        ... some code
 *        case 'your-directive-name':
 *            import(/* webpackChunkName: "your-directive-name" *\/ './your/directive/file/location').then(() => {
 *                resolve();
 *            });
 *            break;
 *        ... some code
 *     }
 *     ... some code
 *
 *     or you could add ./async file in your module's directory and import it into ./src/async.js
 *
 *
 *     // ./src/async.js
 *
 *     ... some code
 *     import yourmodule from './src/yourmodule/async';
 *
 *     ... some code
 *     try{
 *          yourmodule(name, resolve);
 *     }catch(e){
 *          ... some code
 *     }
 *     ... some code
 *
 *
 *     // ./src/yourmodule/async.js
 *
 *     export default function(name, resolve){
 *        switch(name){
 *           case 'your-directive':
 *               import(/* webpackChunkName: "your-directive" *\/ './yourdirective').then(() => {
 *                   resolve();
 *               });
 *               break;
 *           case 'your-directive2':
 *               import(/* webpackChunkName: "your-directive2" *\/ './yourdirective/yourdicrective2').then(() => {
 *                   resolve();
 *               });
 *               break;
 *       }
 *   }
 *
 *
 * 2. you can use the async directive next ways:
 *
 *     a) <your-directive-name async-component></your-directive-name>
 *
 *     b) <div async-component="your-directive-name"></div>
 *
 */
export class AsyncComponentComponent extends Directive {
    public static get $name() {
        return 'asyncComponent';
    }

    public static get $priority() {
        return 100000;
    }

    public static get $terminal() {
        return true;
    }

    public static get $inject() {
        return ['$compile', '$injector'];
    }

    constructor(
        private $compile: any,
        private $injector: any
    ) {
        super();
    }

    private _checkIsComponentRegistered(componentName: string): boolean {
        return this.$injector.has(`${componentName}Directive`);
    }

    public compile($element: JQuery, $attrs: angular.IAttributes): any {
        const chunkName: string = $attrs['asyncComponent'] || ($element[0] && $element[0].tagName.toLowerCase());
        const directiveName: string = chunkName
            .split('-')
            .map((v, k) => (k ? `${v[0].toUpperCase()}${v.slice(1)}` : v))
            .join('');

        $element.removeAttr('async-component');

        return ($scope: angular.IScope, $element: JQuery) => {
            if (!AsyncService.loaded(chunkName)) {
                AsyncService.run(chunkName).then(() => {
                    $scope.$applyAsync(() => {
                        if (this._checkIsComponentRegistered(directiveName)) {
                            this.$compile($element)($scope);
                        } else {
                            throw Error(`Impossible situation, something wrong with your async registration of '${chunkName}'`);
                        }
                    });
                });
            } else {
                $scope.$applyAsync(() => {
                    this.$compile($element)($scope);
                });
            }
        };
    }
}
